import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowLeft, Upload, Download, Printer, Search, MoveRight, Copy } from "lucide-react";
import * as XLSX from "xlsx";

const STATUS = [{ v: "aktif", l: "Aktif" }, { v: "pindah", l: "Pindah" }, { v: "tidak_aktif", l: "Tidak Aktif" }];
const CLASS_LEVELS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
const STUDENT_BOOKS = ["SK2", "BO1", "BO2", "BO3", "BO4", "BO5", "BO6", "LINK1", "LINK2", "LINK3"];
const EMPTY = { nis: "", name: "", class_in_school: "", book_level: "", gender: "", status: "aktif", note: "", number: "" };

export default function ClassStudents() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role !== "coordinator";

  const [cls, setCls] = useState(null);
  const [students, setStudents] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [moveTarget, setMoveTarget] = useState({ open: false, student: null, target: "" });
  const [myClasses, setMyClasses] = useState([]);
  const [copy, setCopy] = useState({ open: false, source: "", classes: [] });

  const load = () => api.get(`/classes/${classId}/students`).then((r) => { setCls(r.data.class); setStudents(r.data.students); }).catch((e) => { toast.error(e.response?.data?.detail || "Gagal memuat"); });
  useEffect(() => { load(); }, [classId]); // eslint-disable-line

  const filtered = useMemo(() => {
    return students.filter((s) => (statusFilter === "all" || s.status === statusFilter) && (!q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.nis || "").includes(q)));
  }, [students, q, statusFilter]);

  const openNew = () => { setForm(EMPTY); setDialog("new"); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s, number: s.number || "" }); setDialog(s.id); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama siswa wajib diisi"); return; }
    const body = { ...form, number: form.number ? Number(form.number) : null };
    try {
      if (dialog === "new") await api.post(`/classes/${classId}/students`, body);
      else await api.put(`/students/${dialog}`, body);
      toast.success("Data siswa tersimpan"); setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus siswa "${s.name}"? Nilai siswa juga dapat terpengaruh. Data dapat dipulihkan Branch Manager.`)) return;
    try { await api.delete(`/students/${s.id}`); toast.success("Siswa dihapus"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openMove = async (s) => {
    const r = await api.get("/classes");
    setMyClasses(r.data.filter((c) => c.id !== classId));
    setMoveTarget({ open: true, student: s, target: "" });
  };
  const doMove = async () => {
    if (!moveTarget.target) { toast.error("Pilih kelas tujuan"); return; }
    try { await api.post(`/students/${moveTarget.student.id}/move`, { target_class_id: moveTarget.target }); toast.success("Siswa dipindahkan"); setMoveTarget({ open: false, student: null, target: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const exportCSV = () => {
    const head = ["No", "NIS", "Nama", "Kelas", "Buku/Level", "Jenis Kelamin", "Status", "Catatan"];
    const rows = filtered.map((s) => [s.number || "", s.nis || "", s.name, s.class_in_school || "", s.book_level || "", s.gender || "", s.status, (s.note || "").replace(/[\n,]/g, " ")]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `Daftar-Siswa-${(cls?.name || "kelas").replace(/\s+/g, "-")}.csv`; a.click();
  };

  const mapRows = (matrix) => {
    // matrix: array of arrays (rows of cells)
    const nonEmpty = matrix.filter((r) => r.some((c) => String(c ?? "").trim()));
    if (!nonEmpty.length) return [];
    let start = 0;
    const first = nonEmpty[0].map((c) => String(c ?? "").toLowerCase()).join(" ");
    if (first.includes("nama") || first.includes("name") || first.includes("nis")) start = 1;
    const rows = [];
    for (let i = start; i < nonEmpty.length; i++) {
      const cols = nonEmpty[i].map((c) => String(c ?? "").trim());
      let name, nis = "", cis = "", book = "", gender = "";
      if (cols.length >= 3) { nis = cols[1]; name = cols[2]; cis = cols[3] || ""; book = cols[4] || ""; gender = cols[5] || ""; }
      else { name = cols[0]; }
      if (name) rows.push({ name, nis, class_in_school: cis, book_level: book, gender, status: "aktif" });
    }
    return rows;
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    let matrix = [];
    try {
      if (/\.(xlsx|xls)$/i.test(file.name)) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        matrix = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
      } else {
        const text = await file.text();
        matrix = text.split(/\r?\n/).map((l) => l.split(",").map((c) => c.replace(/^"|"$/g, "")));
      }
    } catch (err) { toast.error("Gagal membaca file"); e.target.value = ""; return; }
    const rows = mapRows(matrix);
    if (!rows.length) { toast.error("Tidak ada data siswa terbaca"); e.target.value = ""; return; }
    try { const r = await api.post(`/classes/${classId}/students/import`, { students: rows }); toast.success(`${r.data.created} siswa diimpor`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal impor"); }
    e.target.value = "";
  };

  const openCopy = async () => {
    const r = await api.get("/classes");
    setCopy({ open: true, source: "", classes: r.data.filter((c) => c.id !== classId) });
  };
  const doCopy = async () => {
    if (!copy.source) { toast.error("Pilih kelas sumber"); return; }
    try { const r = await api.post(`/classes/${classId}/students/copy-from`, { source_class_id: copy.source }); toast.success(`${r.data.created} siswa disalin`); setCopy({ open: false, source: "", classes: [] }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-4" data-testid="class-students-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center gap-3 no-print">
        <Button variant="outline" size="sm" onClick={() => navigate("/classes")} className="rounded-full gap-1" data-testid="back-to-classes"><ArrowLeft className="w-4 h-4" /> Kembali</Button>
      </div>

      {cls && (
        <Card className="rounded-2xl border-none shadow-sm p-5 bg-white">
          <h2 className="font-nunito font-extrabold text-xl text-gtsnavy">{cls.name}</h2>
          <p className="text-sm text-muted-foreground">{cls.school_name || ""} · Sem {cls.semester} · {cls.academic_year}{cls.class_in_school ? ` · ${cls.class_in_school}` : ""}{cls.book_level ? ` · ${cls.book_level}` : ""}</p>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2 no-print">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / NIS" className="pl-9 rounded-xl w-52" data-testid="student-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-xl w-40" data-testid="student-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem>{STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex-1" />
        {canWrite && <Button onClick={openNew} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="add-student-btn"><Plus className="w-4 h-4" /> Siswa</Button>}
        {canWrite && (
          <label>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={importFile} data-testid="import-students-input" />
            <span className="inline-flex items-center gap-1 rounded-full border px-4 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid="import-students-btn"><Upload className="w-4 h-4" /> Import</span>
          </label>
        )}
        {canWrite && <Button variant="outline" onClick={openCopy} className="rounded-full gap-1" data-testid="copy-students-btn"><Copy className="w-4 h-4" /> Salin</Button>}
        <Button variant="outline" onClick={exportCSV} className="rounded-full gap-1" data-testid="export-students-btn"><Download className="w-4 h-4" /> Export</Button>
        <Button variant="outline" onClick={() => window.print()} className="rounded-full gap-1" data-testid="print-students-btn"><Printer className="w-4 h-4" /> Print</Button>
      </div>

      <Card className="rounded-2xl border-none shadow-sm bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="students-table">
          <thead className="bg-gtsnavy/5 text-gtsnavy">
            <tr>
              <th className="px-3 py-3 text-left w-12">No</th>
              <th className="px-3 py-3 text-left">NIS</th>
              <th className="px-3 py-3 text-left">Nama Siswa</th>
              <th className="px-3 py-3 text-left">Kelas</th>
              <th className="px-3 py-3 text-left">Buku/Level</th>
              <th className="px-3 py-3 text-left">JK</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-right no-print">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground" data-testid="students-empty">Belum ada siswa.</td></tr>
            ) : filtered.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`student-row-${s.id}`}>
                <td className="px-3 py-2.5">{s.number}</td>
                <td className="px-3 py-2.5">{s.nis || "-"}</td>
                <td className="px-3 py-2.5 font-medium text-gtsnavy">{s.name}</td>
                <td className="px-3 py-2.5">{s.class_in_school || "-"}</td>
                <td className="px-3 py-2.5">{s.book_level || "-"}</td>
                <td className="px-3 py-2.5">{s.gender || "-"}</td>
                <td className="px-3 py-2.5"><Badge className={`rounded-full ${s.status === "aktif" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{STATUS.find((x) => x.v === s.status)?.l || s.status}</Badge></td>
                <td className="px-3 py-2.5 text-right no-print">
                  {canWrite && (
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="rounded-full" data-testid={`edit-student-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => openMove(s)} className="rounded-full" data-testid={`move-student-${s.id}`}><MoveRight className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(s)} className="rounded-full text-rose-600 border-rose-200" data-testid={`delete-student-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog === "new" ? "Tambah Siswa" : "Edit Siswa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomor</Label><Input type="number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="rounded-xl mt-1" placeholder="otomatis" data-testid="student-number" /></div>
              <div><Label>NIS</Label><Input value={form.nis || ""} onChange={(e) => setForm({ ...form, nis: e.target.value })} className="rounded-xl mt-1" data-testid="student-nis" /></div>
            </div>
            <div><Label>Nama Lengkap *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" data-testid="student-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kelas di Sekolah</Label>
                <Select value={form.class_in_school || ""} onValueChange={(v) => setForm({ ...form, class_in_school: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="student-class"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                  <SelectContent>{CLASS_LEVELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Buku / Level</Label>
                <Select value={form.book_level || ""} onValueChange={(v) => setForm({ ...form, book_level: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="student-book"><SelectValue placeholder="Pilih buku" /></SelectTrigger>
                  <SelectContent>{STUDENT_BOOKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Jenis Kelamin</Label>
                <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="student-gender"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="student-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Catatan</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-xl mt-1" data-testid="student-note" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-gtsnavy text-white" data-testid="save-student-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={copy.open} onOpenChange={(o) => !o && setCopy({ open: false, source: "", classes: [] })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Salin Siswa dari Kelas Lain</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Salin seluruh daftar siswa aktif dari kelas lain (mis. semester sebelumnya) ke kelas ini.</p>
            <Select value={copy.source} onValueChange={(v) => setCopy({ ...copy, source: v })}>
              <SelectTrigger className="rounded-xl" data-testid="copy-source-select"><SelectValue placeholder="Pilih kelas sumber" /></SelectTrigger>
              <SelectContent>{copy.classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.school_name} · Sem {c.semester} {c.academic_year}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={doCopy} className="rounded-full bg-gtsnavy text-white" data-testid="confirm-copy-btn">Salin Siswa</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveTarget.open} onOpenChange={(o) => !o && setMoveTarget({ open: false, student: null, target: "" })}>        <DialogContent>
          <DialogHeader><DialogTitle>Pindahkan Siswa</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Pindahkan <b>{moveTarget.student?.name}</b> ke kelas lain.</p>
            <Select value={moveTarget.target} onValueChange={(v) => setMoveTarget({ ...moveTarget, target: v })}>
              <SelectTrigger className="rounded-xl" data-testid="move-target-select"><SelectValue placeholder="Pilih kelas tujuan" /></SelectTrigger>
              <SelectContent>{myClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.school_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={doMove} className="rounded-full bg-gtsnavy text-white" data-testid="confirm-move-btn">Pindahkan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
